import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useCode } from '../../hooks/codes';
import resourcesRepository from '../../lib/resource';
import interactionRepository from '../../lib/interaction';
import getPosition from '../../services/getPosition';

import Loading from '../../components/Loading';
import Details from '../../components/Details';
import ProductByCode from '../../components/ProductByCode';
import ResourceDTO from '../../dtos/Resource';

import {
  Container,
  Header,
  HeaderBrand,
  HeaderRight,
  Logo,
  HeaderSearch,
  PageBody,
  HomeCentered,
  HomeLogo,
  HomeSearch,
  HomeInstruction,
} from './styles';

interface Params {
  value?: string;
}

const CodeInfo: React.FC = () => {
  const [search, setSearch] = useState('');
  const { code, loading, find } = useCode();

  const { value } = useParams<Params>();
  const history = useHistory();

  const [latLng, setLatLng] = useState<{ lat: number; lng: number } | null>(null);
  const [resource, setResource] = useState<ResourceDTO>();
  const [loadingResources, setLoadingResources] = useState(false);

  const [searchAttempted, setSearchAttempted] = useState(!!value);
  const [searchFinished, setSearchFinished] = useState(false);

  const lastToastCodeRef = useRef<string | null>(null);

  useEffect(() => {
    getPosition().then((pos) => {
      if (pos?.latitude && pos?.longitude) {
        setLatLng({ lat: pos.latitude, lng: pos.longitude });
      }
    });
  }, []);

  const findResources = useCallback(
    async (id: string) => {
      setLoadingResources(true);
      try {
        const data = await resourcesRepository.find(id, value || '');
        if (!data) {
          throw new Error(`Não foram encontrados recursos para o ID: ${id}`);
        }
        setResource(data);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        toast.error(errorMessage);
      } finally {
        setLoadingResources(false);
      }
    },
    [value]
  );

  useEffect(() => {
    if (code?.resourceId) {
      findResources(code.resourceId);
    }
  }, [code, findResources]);

  useEffect(() => {
    if (value) {
      setSearchAttempted(true);
      lastToastCodeRef.current = null;
      setSearchFinished(false);
      setSearch(value); // sincroniza input com url

      find(value)
        .then(() => setSearchFinished(true))
        .catch(() => setSearchFinished(true));
    } else {
      // Se apagar a URL manualmente, volta pra tela inicial
      setSearchAttempted(false);
      setSearchFinished(true);
    }
  }, [value, find]);

  // Toast origin rule
  useEffect(() => {
    if (!searchAttempted || loading || !searchFinished) return;
    if (!code || Object.keys(code).length === 0) return;
    if (!code.value) return;

    const displayStatusIsNull = code.resource?.displayStatus === null;

    if (!code.resourceId || displayStatusIsNull) {
      if (lastToastCodeRef.current !== code.value) {
        toast.warn('Garantia de origem controlada');
        lastToastCodeRef.current = code.value;
      }
    } else {
      lastToastCodeRef.current = null;
    }
  }, [searchAttempted, loading, searchFinished, code]);

  useEffect(() => {
    if (resource?.id && latLng) {
      const saveInteractionView = async () => {
        try {
          await interactionRepository.saveInteraction({
            resourceId: resource.id,
            code: code.value,
            latitude: latLng.lat,
            longitude: latLng.lng,
            status: 'Envasado',
          });
        } catch (error) {
          // silent failure para métricas
        }
      };
      saveInteractionView();
    }
  }, [resource, latLng, code]);

  const handleClearSearch = () => {
    setSearch('');
    setSearchAttempted(false);
    history.push('/');
  };

  const handleSearch = (event: React.FormEvent<Element>) => {
    event.preventDefault();

    if (!search.trim()) {
      toast.error('Digite um código para buscar.');
      return;
    }

    setSearchAttempted(true);
    lastToastCodeRef.current = null;
    history.push(`/${search.trim()}`);
  };

  // ── Renderização da Home Inicial ──
  if (!searchAttempted && !value) {
    return (
      <Container>
        <HomeCentered>
          <HomeLogo />
          <HomeSearch
            placeholder="Pesquisar código do produto"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onSubmit={handleSearch}
            clearSearch={() => setSearch('')}
            disabled={loading}
          />
          <HomeInstruction>
            Digite o código unitário na barra de busca acima
            para acessar o relatório completo de origem, informações técnicas e o
            histórico do produto.
          </HomeInstruction>
        </HomeCentered>
      </Container>
    );
  }

  // ── Renderização dos Resultados ──
  return (
    <Container>
      <Header>
        <HeaderBrand>
          <Logo />
        </HeaderBrand>

        <HeaderSearch
          placeholder="Pesquisar outro código"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onSubmit={handleSearch}
          clearSearch={handleClearSearch}
          disabled={loading}
        />

        <HeaderRight />
      </Header>

      <PageBody>
        {loading ? (
          <Loading text="Buscando informações do produto..." />
        ) : code?.resourceId ? (
          <ProductByCode
            qrCode={code}
            resource={resource}
            loadingResources={loadingResources}
          />
        ) : (
          <Details code={code} />
        )}
      </PageBody>
    </Container>
  );
};

export default CodeInfo;
